
import { rememberDisplayedAnswers } from './ui/extras.js';
import { fetchRequest } from './api/api.js';
import { actions } from "./actions/action-config.js";
import { runActionPhase } from "./actions/action-functions.js";
import { getBookingState, getBookingStateProp, setBookingStateProp } from "./state.js";
import { onStepperButtonClick, syncStepperButtons } from "./ui/stepper.js";
import { handleNextStage, handleReturnToStage } from './stage-manager.js';
import { dom, elById, qs, qsAll } from "./utils/dom.js";
import { attachXCoverRadioHandler } from "./custom_extras/xcover/xcover.js";
import { updateSummary } from "./ui/checkout-summary.js";
import { getFormValues } from "./form/form-handling.js";
import { validateDiscountInput } from "../checkout/ui/discount.js";
import { setControlValidationState } from './utils/validation-utils.js';
import { AvailabilityType } from "./enums/availability-type.js";
import { calendar } from "./checkout-visual-calendar.js";
import { setDropdown } from "./ui/dropdown.js";
import { toggle } from "./utils/ui-helpers.js";
import { buildSubUnitSelectionRequest } from "./utils/sub-unit-selection.js";

const DEFAULT_PARTY_SIZE = 1;

// TODO: the number of listeners being added may soon warrant separate files for inputs like steppers that have multiple handlers
// and async modules like multiple-booking
/**
 * Attaches event listeners for checkout stage handling and other checkout related events
 */
export function init() {
    console.log('Attaching Event Listeners');
    
    // Stage Event listeners 
    dom.addEventListener('click', (event) => {
        if (!event.target.matches('.atb-next-stage-btn') && !event.target.matches('#atb-place-booking-button')) return;

        const stageEl        = event.target.closest('.atb-checkout-accordion-item');
        const currentStageId = stageEl.dataset.stageId;

        handleNextStage(currentStageId);
    });

    // Return event listener
    dom.addEventListener('click', (event) => {    
        const accordionBtn = event.target.closest('.atb-checkout-accordion-button');
        if (!accordionBtn) return;

        console.log('clicked accordion button');

        const stageEl       = accordionBtn.closest('.atb-checkout-accordion-item');
        const targetStageId = stageEl.dataset.stageId;

        handleReturnToStage(targetStageId);
    });


    // Initialize steppers
    // TODO: maybe move to the steppers module and stick in a .init function that can be called from here
    qsAll('.atb-stepper').forEach(stepper => {
        const counter = stepper.querySelector('.counter');
        if (!counter) return;
        
        const min   = parseInt(counter.dataset.min, 10);
        const max   = parseInt(counter.dataset.max, 10);
        const value = parseInt(counter.value, 10) || 0;

        counter.value = value;

        syncStepperButtons(stepper, value, min, max);
    });


    // Stepper event listeners
    dom.addEventListener('click', onStepperButtonClick);

    // Attach event listener for 'stage complete' custom event
    dom.addEventListener('StageComplete', onStageComplete);

    dom.addEventListener('click', async (event) => {
        // Apply discount code
        const applyBtn = event.target.closest('.discount-code-btn');
        if (applyBtn) {
            // Guard against double-submits (a second tap while the first request is still in flight
            // would race and trip the "already applied" branch of validateDiscountInput)
            if (applyBtn.disabled) return;

            const container = applyBtn.closest('.atb-discount-input-field');
            const input     = container?.querySelector('input');

            if (!input) return;

            const isValid = validateDiscountInput({ target: input });

            if (!isValid) return;

            applyBtn.disabled = true;

            try {
                const result = await handleInputChange(input);
                if (result?.data?.discount_ok) {
                    input.value = '';
                } else if (result?.data?.discount_ok === false) {
                    const feedback = container.querySelector('.invalid-feedback');
                    input.classList.add('is-invalid');
                    if (feedback) {
                        feedback.textContent = 'This code cannot be applied to this booking. Any existing discount remains in place.';
                        feedback.classList.remove('d-none');
                    }
                    setControlValidationState(input, feedback, false);
                }
            } finally {
                applyBtn.disabled = false;
            }
        }
    });

    // Separate document-level listener for dynamically rendered discount tags
    dom.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('.atb-discount-tag[data-discount-code]');
        if (!removeBtn) return;

        const closeBtn = removeBtn.querySelector('.close-btn');
        handleInputChange(closeBtn);
    });

    // Stage 1 dropdowns / payment selectors
    dom.addEventListener('change', (event) => {
        const inputEl = event.target.closest('input[data-input]');
        if (!inputEl) return;

        // The discount field is applied explicitly via the "Apply" button (see the .discount-code-btn
        // handler above). Letting it through here as well means tapping Apply blurs the input, fires
        // 'change', and sends a second identical request to /checkout/set/discount_code/ - which then
        // races the click handler's request and trips the "already applied" branch of validateDiscountInput.
        if (inputEl.dataset.input === 'discount_code') return;

        handleInputChange(inputEl);
    });

    // XCover related event handlers
    attachXCoverRadioHandler(handleInputChange);
}

// A small adapter for handling custom dropdown's 'change' events
function handleInputEvent(event) {
    // Find the closest element with [data-input]
    const el = event.target.closest('[data-input]');
    if (!el) return;

    handleInputChange(el);
}


// TODO: not sure I like the name of this function if I continue to use it as a universal event handler
export async function handleInputChange(el) {
    if (!el) return;

    if (!el.dataset) {
        console.warn("Element does not have data attributes");
        return;
    }
    
    const inputId = el.dataset.input;
    // offsetParent is null if element is hidden
    //TODO: currently, the sub-unit input sometimes requires us to set it while it's not visible - find a more elegant way of dealing with this, we will forget this edgecase detail
    // and have to read the implementation of handleInputChange to understand
    if (!el.offsetParent && inputId !== 'sub_unit') { 
        if (el.hasAttribute('data-is-js-template') || inputId?.startsWith('type_1_'))
            return;

        console.warn(`handleInputChange called on hidden element "${inputId}"`);
        return;
    }

    // unified key       
    if (!inputId) {
        console.warn("Element does not have universal data attribute 'data-input'");
        return;
    }

    const action = actions[inputId];
    if (!action) {
        if (el.closest('#atb-main-party-section'))
            return;

        console.warn(`No existing handler in 'actions' for ID ${inputId}`);
        return;
    }

    // Extract value from input
    const value = extractInputValue(el);
    if (value === undefined) {
        console.warn('Input value is undefined, not proceeding');
        return;
    } 
    
    if (inputId === 'addon') rememberDisplayedAnswers();

    if (value == getBookingState()[inputId]) {      
        console.warn('Input value has not changed, not proceeding');  
        return;
    }

    return await runInputAction(inputId, value, el);
}

// Serialize extra writes so an older answer cannot overwrite a newer one.
let addonSaveQueue = Promise.resolve();
function runInputAction(inputId, value, el = null) {
    if (inputId !== 'addon') return executeInputAction(inputId, value, el);
    const save = addonSaveQueue.then(() => executeInputAction(inputId, value, el));
    addonSaveQueue = save.catch(() => undefined);
    return save;
}

export async function saveExtrasBeforeContinue() {
    await addonSaveQueue;
    // Submit every displayed answer, including defaults and unchanged values.
    // Only submit the primary control; extraction includes its additional answer.
    const inputs = document.querySelectorAll('#atb-extras-section .atb-addon input[data-input="addon"]');
    for (const input of inputs) {
        if (!input.offsetParent || input.closest('.atb-addon')?.dataset.checkoutApplicable === '0' || input.dataset.customAddonType ||
            input.dataset.additionalAnswer || input.dataset.dropdownOptionAnswer) continue;
        const result = await runInputAction('addon', extractInputValue(input), input);
        if (!result) throw new Error('We could not save your extras. Please try Continue again.');
    }
    await addonSaveQueue;
    const response = await fetchRequest('/checkout/validateExtras/');
    const result = await response.json();
    if (!response.ok || !result.success) {
        throw new Error(result.error || 'We could not save your extras. Please try Continue again.');
    }
}

async function executeInputAction(inputId, value, el = null) {
    const action = actions[inputId];

    let context = {
        inputId,
        value,
        el,
        data: null
    };

    // Run fetch/set action - not all elements use this
    if (action.fetch) {
        context.data = await action.fetch(
            context.inputId,
            buildRequestObject(context.inputId, context.value)
        );

        if (context.data?.response !== 'ok') return;
        if (getBookingStateProp('has_run_refund_quote'))
            context = await removeRefundQuote(context);
    }

    // Set GlobalState using data retrieved in async action
    const nextAction = action.set?.(context.value, context.data);

    if (inputId === 'unit' && getBookingStateProp('availability_type') !== AvailabilityType.Occupancy) {
        const defaultPartySize = getBookingStateProp('party_size') || DEFAULT_PARTY_SIZE;
        if (getBookingStateProp('using_party_size'))
            setDropdown(elById('atb-party-size'), String(defaultPartySize), false);

        await runInputAction('party_size', defaultPartySize);
    }

    // Update the UI
    action.render?.(context.data);

    // if (typeof nextAction !== 'undefined')
    //     runAction(nextAction, 'all', context);

    return context;
}


function extractInputValue(el) {
    const currentBookingData = getBookingState();

    // Handle addons + custom addons (like xcover, motorhome hire, etc.)
    if (el.dataset.input == 'xcover') {
        return extractCustomAddonValue(el);
    }

    if (el.dataset.input == 'addon')
        return extractAddonValue(el);

    // If the input belongs to a custom dropdown, return with the data attribute value
    if (el.dataset.selectedId !== undefined) {        
        return el.dataset.selectedId;
    }

    // TODO: can probably generalise for all stepper types
    if (el.dataset.stepperType === 'party') {
        const partyMembers     = currentBookingData.party_members;
        const key              = parseIntb10(el.dataset.entityId);
        partyMembers['_'+key].qty = parseIntb10(el.value);
        
        return partyMembers;
    }

    // multi-bookings
    if (el.dataset.input == 'delete_booking') {
        // NOTE: /checkout/removeBooking/ expects the "booking number", not the index so add 1
        return parseIntb10(el.dataset.bookingIndex);
    }

    if (el.dataset.input == 'payment_select') {
        return el.dataset.paymentType;
    }

    // Otherwise, handle regular inputs
    switch (el.type) {
        case 'checkbox':
            return el.checked;

        case 'radio':
            if (!el.checked) return undefined;
            return el.value;

        // case 'text':
        // case 'email':
        // case 'number':
        // case 'tel':

        default:
            return el.value;
    }
}


/**
 * Extracts the value from 'Extras' related inputs, and builds objects that are request-ready
 */
function toggleDropdownOptionAnswer(addonId, dropdownOptionId) {
    const answerRows = document.querySelectorAll(`#atb-addon-${addonId} .atb-addon-dropdown-answer`);
    let selectedAnswerEl = null;

    answerRows.forEach(row => {
        const showRow = row.dataset.dropdownOptionId == dropdownOptionId;
        row.classList.toggle("d-none", !showRow);
        row.style.display = showRow ? "" : "none";

        const input = row.querySelector("input[data-dropdown-option-answer]");
        if (!input) return;

        const isMandatory = showRow && parseInt(input.dataset.dropdownOptionMandatory || 0, 10) == 1;
        input.required = isMandatory;
        input.classList.toggle("atb-required-customer-field", isMandatory);

        if (showRow) selectedAnswerEl = input;
        else input.value = "";
    });

    return selectedAnswerEl;
}

/**
 * Extracts the value from Extras related inputs, and builds objects that are request-ready
 */
function extractAddonValue(el, bookingData) {
    const builAddonObject = ({id, dropdownOptionId, value = "", value2 = "", inputType}) => ({
        addon_id          : id,
        dropdown_option_id: dropdownOptionId ?? 0,
        addon_value       : value ?? "",
        addon_value2      : value2 ?? "",
        input_type        : inputType ?? ""
    });

    const { entityId, selectedId = null, stepperType = null } = el.dataset;

    // Additional answer text input for the selected option of a dropdown extra.
    if (el.dataset.dropdownOptionAnswer) {
        const dropdownEl = document.getElementById(`atb-${entityId}`);
        const dropdownOptionId = dropdownEl?.dataset.selectedId || el.dataset.dropdownOptionId || 0;
        return builAddonObject({
            id: entityId,
            value: dropdownEl?.value || "",
            value2: el.value.trim(),
            dropdownOptionId,
            inputType: "dropdown"
        });
    }

    // Addon dropdowns
    if (selectedId) {
        console.log("Extracting addon dropdown value");
        const answerEl = toggleDropdownOptionAnswer(entityId, selectedId);
        return builAddonObject({
            id: entityId,
            value: el.value,
            value2: answerEl ? answerEl.value.trim() : "",
            dropdownOptionId: selectedId,
            inputType: "dropdown"
        });
    }

    // Additional answer text input accompanying a stepper or checkbox extra
    if (el.dataset.additionalAnswer) {
        const counter  = document.getElementById(`counter-addon-${entityId}`);
        const checkbox = document.getElementById(`atb-addon-${entityId}`);
        if (counter) {
            return builAddonObject({id: entityId, value: parseInt(counter.value, 10), value2: el.value.trim(), inputType: "stepper"});
        } else if (checkbox) {
            return builAddonObject({id: entityId, value: checkbox.checked ? 1 : 0, value2: el.value.trim(), inputType: "checkbox"});
        }
        return builAddonObject({id: entityId, value2: el.value.trim()});
    }

    // Addon steppers - also carry any additional answer text alongside the counter value
    if (stepperType) {
        console.log("Extracting addon stepper value");
        const additionalAnswerEl = document.getElementById(`atb-${stepperType}-${entityId}-additional-answer`);
        const value2 = additionalAnswerEl ? additionalAnswerEl.value.trim() : "";
        return builAddonObject({id: entityId, value: parseInt(el.value, 10), value2, inputType: "stepper"});
    }

    // Otherwise, handle regular inputs
    switch (el.type) {
        case "checkbox": {
            const additionalAnswerEl = document.getElementById(`atb-addon-${entityId}-additional-answer`);
            const value2 = additionalAnswerEl ? additionalAnswerEl.value.trim() : "";
            return builAddonObject({id: entityId, value: el.checked ? 1 : 0, value2, inputType: "checkbox"});
        }

        // TODO: support text extras

        // case "radio":
        //     if (!el.checked) return undefined;
        //     return el.value;

        // case "text":
        // case "email":
        // case "number":
        // case "tel":

        default:
            return builAddonObject({id: entityId, value: el.value, inputType: el.type});
    }
}

function extractCustomAddonValue(el) {
    // TODO: likely move this out as it's used in one other place
    const builObject = ({value, customType}) => ({
        addon_id: "", // custom extras have no ID because they don't exist in the database
        addon_value: value,
        custom_type: customType,
        custom: 1
    });

    const { input, value } = el.dataset;

    if (input == 'xcover') {        
        return builObject({ value: parseInt(value, 10), customType: input });
    }

    // if (entityId == 'thl') {        
    // the addon value is now an object witht he original inputs value, plus the special_value specific to the custom extra
    // e.g. thl = 0:<search_result_url> (0 being no, 1 being yes)
            //'c-'+customExtraType+':'+finalCustomValue,
    //     return builObject({ value: parseInt(value, 10), custom_type: entityId });
    // }
}


/**
 * Builds a request object for the specific key, ready to send to PHP session
 * @param {string} bookingKey - represents the key in the booking session object
 * @param {string | number | object} value - the value extracted from the input element
 * @returns {object} A request object specific to the input type
 */
function buildRequestObject(bookingKey, value) {
    const bookingData = getBookingState();
    switch(bookingKey) {
        case 'site': {
            return null;
        }
        case 'category': {
            return {
                category: value,
                auto_select_unit: 0,
                back_office: false,                
            }
        }
        case 'unit': {            
            return {
                agent          : 0, // TODO: is this needed?
                category       : bookingData.category,
                unit           : value,
                show_past_dates: 0,
                back_office    : false
            }
        }
        case 'party_size': {
            return {
                party_size: value
            }
        }
        case 'party': {
            const partyMemberIds    = Object.keys(value);
            const partyMemberValues = Object.values(value);

            return {
                party_member_ids   : partyMemberIds.map(id => id.slice(1)),
                party_member_values: partyMemberValues.map(valueObj => valueObj.qty)
            }
        }
        case 'addon': {            
            return value;
        }
        case 'xcover': {
            return value;
        }
        case 'delete_booking': {
            return { booking_number: value }
        }
        case 'payment_select': {        
            return { 
                payment_type: value,
                booking_number: 0 // NOTE: THis should ALWAYS be 0, as the newest booking sits at index 0 in session
            }
        }
        case 'sub_unit': {
            return buildSubUnitSelectionRequest(value);
        }
        default: {
            console.log('No special request object builder for key ', bookingKey, '\n setting', bookingKey, value);
            return { [bookingKey]: value };
        }
    }
}


async function removeRefundQuote(context) {
    const useBookingProtect  = getBookingStateProp('use_booking_protect');
    const hasRunQuote        = getBookingStateProp('has_run_refund_quote');
    const removeQuoteActions = [ 'site', 'category', 'unit', 'period', 'sub_unit', 'party', 'addon' ];

    // If the booking is using booking protect, remove it if we're running any of the actions in removeQuote
    if (
        useBookingProtect && 
        hasRunQuote && 
        removeQuoteActions.includes(context.inputId)
    ) {
        const requestObj = {
            addon_id: '', // custom extras have no ID because they don't exist in the database
            addon_value: 0,
            custom_type: 'xcover',
            custom: 1
        };

        const setResponse = await runActionPhase('xcover', 'fetch', {value: '', data: requestObj});
        updateSummary(setResponse.booking_info);

        // TODO: don't like setting state outside of "action-config", look to refactor this but this is unavoidable for now
        setBookingStateProp('has_run_refund_quote', false);
        
        context.data = setResponse;
    }

    return context;
}


/**
 * A handler function for performing business logic related to the completion of checkout stages
 * 
 * @param {*} stageCompleteEvent the event object dispatched when a stage is completed, found in stage-complete-event.js
 */
export async function onStageComplete(stageCompleteEvent) {
    const { stage, nextStage } = stageCompleteEvent.detail;

    // TODO: REFACTOR - will avoid hardcoding names like this in the future, see stage-manager.js comments
    switch (stage) {
        case "booking": {
            await buildPartyMemberDetailsSection();

            if (nextStage !== "extras")
                populateFirstPartyDetailsIfLoggedIn();

            break;
        }
        case "extras": {
            // If the user entered the checkout logged in already, their login details will have been loaded into the form, but the party member details sections are generated
            // programmatically at the end of Stage 1, so the first "adult" party member will be empty
            // pull them out of the lead booker form and populate the first adult party member details section
            populateFirstPartyDetailsIfLoggedIn();
            break;
        }
        case "party": {

            // Temp fix: copy email field from login area into billing details area
            // $('#atb-email').val($('#atb-login-email').val());
            elById('atb-email').value = elById('atb-login-email').value;

            // TODO: may need to move this submission code back into "isStage3Valid" - need to make sure these forms have submitted before moving to payment stage
            const leadBookerFormData = getFormValues(elById('atb-party-details-lead-booker'));
            const partyDetailsFormData = getFormValues(elById('atb-party-details-form'));
            
            let billingResponse = await runActionPhase('set_billing_details', 'fetch', { value: 'billing_details', data: leadBookerFormData });
            let partyDetailsResponse = await runActionPhase('set_party_details', 'fetch', { value: 'party_details', data: partyDetailsFormData });

            await onCaravanClubNumChanged();

            break;
        }
        case "payment": {
            console.log('placing booking');
            placeBooking();
            
            break;
        }
    }
}

function populateFirstPartyDetailsIfLoggedIn() {
    if (isLoggedIn())
        populateFirstPartyDetailsFromLeadBooker();
}

export async function setStageOne(clientData) {
    const hasOneCategory = clientData.categories && clientData.categories?.length == 1;
    const hasOneUnit     = clientData.units && clientData.units?.length      == 1;
    const presetSiteId = parseIntb10(clientData.categoryPresetSiteId) || parseIntb10(clientData.sitePresetId);
    const presetSite = clientData.sites?.find(
        site => parseIntb10(site.id) === presetSiteId
    );
    const presetCategoryId = parseIntb10(clientData.categoryPresetId);
    const presetCategory = clientData.categories?.find(
        category => parseIntb10(category.id) === presetCategoryId
    );

    let unit = null;
    if (presetSite) {
        await runInputAction('site', presetSiteId);

        const siteDropdown = elById('atb-site');
        if (siteDropdown)
            setDropdown(siteDropdown, String(presetSiteId), false);

        // A site-only preset leaves category and unit selection to the customer.
        toggle(elById('atb-loading-overlay-spinner'), false);
    }
    if (presetCategory) {
        await runInputAction('category', presetCategory.id);
        setDropdown(elById('atb-category'), String(presetCategory.id), false);

        // A category-only preset deliberately leaves unit selection to the
        // customer, so it cannot rely on updateOnUnitChange() to dismiss the
        // initial loader as a full unit preset does.
        toggle(elById('atb-loading-overlay-spinner'), false);
    } else if (hasOneCategory) {
        console.warn('Client has only one category - defaulting category to the only option');
        const category = clientData.categories[0];
        await runInputAction('category', category.id);
        setDropdown(elById('atb-category'), String(category.id), false);
        
        toggle(elById('atb-category-row'), false);
    }
    
    if (hasOneUnit) {
        console.warn('Client has only one unit - defaulting unit to the only option');
        unit = clientData.units[0];
        setDropdown(elById('atb-unit'), String(unit.id), false);
        await runInputAction('unit', unit.id);

        toggle(elById('atb-unit-row'), false);
    }

    
    // category/unit are hidden
    if (
        hasOneCategory &&
        hasOneUnit
    ) {
        // TODO: restructure the mark-up so we don't have to hide individual HRs - doing this so there isn't an orphaned "hr" when
        toggle(qs('#atb-category-unit-section hr'), parseIntb10(unit.availability_type) == AvailabilityType.Occupancy);        
        
        if (parseIntb10(unit.availability_type) !== AvailabilityType.Occupancy) {
            calendar.generate('', 0, { usingPreset: false, findFirstAvailable: true });
        
            toggle(elById('atb-calendar-row'), true);
            toggleInputDrawer('calendar', true);
        }
    }
}
